import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';

import * as style from "../../styles";
import bg from "../../assets/images/bg_photo.png";

// Add chart data
const chartData = [
  { month: '1', value: 17500 },
  { month: '2', value: 18200 },
  { month: '3', value: 16800 },
  { month: '4', value: 3000 },
  { month: '5', value: 0 },
  { month: '6', value: 0 },
  { month: '7', value: 0 },
  { month: '8', value: 0 },
  { month: '9', value: 0 },
  { month: '10', value: 0 },
  { month: '11', value: 0 },
  { month: '12', value: 0 },
];

const Dashboard = () => {
  const [randomNumber, setRandomNumber] = useState(0);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const generateRandomNumber = () => {
      return Math.floor(Math.random() * 11) + 45;
    };

    const intervalId = setInterval(() => {
      setRandomNumber(generateRandomNumber());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    // Update time immediately
    setCurrentTime(new Date());
    
    // Set up interval to update time every 5 minutes
    const timeIntervalId = setInterval(() => {
      setCurrentTime(new Date());
    }, 5 * 60 * 1000); // 5 minutes in milliseconds
    
    return () => clearInterval(timeIntervalId);
  }, []);

  // Format the date to match the existing format
  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}/${month}/${day} ${hours}:${minutes}`;
  };

  return (
    <Box
      sx={{
        width: "100vw",
        p: "40px",
        display: "flex",
        flexDirection: "column",
        gap: "32px",
      }}>
      <Box sx={{ width: "calc(100vw - 80px)" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}>
          <Typography variant="h2" sx={style.headerTextStyle}>
            溫室氣體盤查整合｜專案列表
          </Typography>
          <Typography>最新資料時間：{formatDate(currentTime)}</Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "stretch",
          alignItems: "center",
          gap: "32px",
        }}>
        <Box
          sx={{
            width: "calc(50vw - 56px - 80px)",
            background:
              "linear-gradient(93.82deg, rgba(188, 0, 0, 0.4) 7.93%, rgba(255, 255, 255, 0) 95.6%), #FBBB0A;",
            minHeight: "50px",
            borderRadius: "15px",
            position: "relative",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            p: "40px",
          }}>
          <Box>
            <Typography
              sx={{
                color: "#ffffff",
                fontSize: "15px",
                lineHeight: "18px",
              }}>
              今日用電量
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
            }}>
            <Typography
              sx={{
                color: "#ffffff",
                fontSize: "47px",
                lineHeight: "55px",
                fontWeight: 700,
              }}>
              {50 + 60 + 70 + 35}
            </Typography>
            <Typography
              sx={{
                color: "#ffffff",
                fontSize: "15px",
                lineHeight: "18px",
                transform: "translateX(10px)",
              }}>
              kWH
            </Typography>
          </Box>
          <Box
            sx={{
              position: "absolute",
              right: "-2px",
              bottom: "-2px",
            }}>
            <img src={bg} alt="bg" />
          </Box>
        </Box>
        <Box
          sx={{
            width: "calc(50vw - 56px - 80px)",
            background:
              "linear-gradient(115.02deg, #016665 17.73%, #16E4B4 102.64%);",
            minHeight: "50px",
            borderRadius: "15px",
            position: "relative",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            p: "40px",
          }}>
          <Box>
            <Typography
              sx={{
                color: "#ffffff",
                fontSize: "15px",
                lineHeight: "18px",
              }}>
              今日天然氣流量合計(未開放本服務)
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
            }}>
            <Typography
              sx={{
                color: "#ffffff",
                fontSize: "47px",
                lineHeight: "55px",
                fontWeight: 700,
              }}>
              --
            </Typography>
            <Typography
              sx={{
                color: "#ffffff",
                fontSize: "15px",
                lineHeight: "18px",
                transform: "translateX(10px)",
              }}>
              立方米
            </Typography>
          </Box>
          <Box
            sx={{
              position: "absolute",
              right: "-2px",
              bottom: "-2px",
            }}>
            <img src={bg} alt="bg" />
          </Box>
        </Box>
      </Box>
      <Box sx={{ width: "calc(100vw - 80px)", display: "flex", gap: "32px" }}>
        <Box
          sx={{
            background: "#ffffff",
            borderRadius: "8px",
            width: "calc(50vw - 56px - 80px)",
            p: "40px",
            gap: "30px",
            display: "flex",
            flexDirection: "column",
          }}>
          <Box
            sx={{
              display: "flex",
              gap: "30px",
              justifyContent: "center",
              alignItems: "center",
            }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                justifyContent: "center",
                alignItems: "center",
              }}>
              <Typography
                sx={{
                  letterSpacing: "0.15px",
                  fontSize: "12px",
                  lineHeight: "12px",
                  fontWeight: 400,
                }}>
                [ 電表 ]
              </Typography>
              <Typography
                sx={{
                  letterSpacing: "0.15px",
                  fontSize: "12px",
                  lineHeight: "12px",
                  fontWeight: 400,
                  ">span": {
                    color: "#FBBB0A",
                    fontSize: "30px",
                    lineHeight: "24px",
                    fontWeight: 700,
                  },
                }}>
                區間用電{" "}
                <span>{17500 + 18200 + 16800 + 3078}</span> kWH
              </Typography>
            </Box>
            <Box sx={{ width: "30vw", height: "200px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[15000, 20000]}
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    tickFormatter={(value: number) => `${value / 1000}k`}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#FBBB0A"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
          <Box sx={{ width: "100%", display: "flex", gap: "16px" }}>
            <TextField
              size="small"
              label="區間－起"
              focused
              value={"2025/01/01"}
              fullWidth
              disabled
              variant="standard"
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  borderRadius: "4px",
                  padding: "4px 8px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(0, 0, 0, 0.6)",
                  fontSize: "0.85rem",
                },
                "& .MuiInputBase-input": {
                  color: "#000000 !important",
                  fontWeight: 500,
                  WebkitTextFillColor: "#000000 !important",
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#000000 !important",
                  color: "#000000 !important",
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottom: "none",
                },
              }}
            />
            <TextField
              size="small"
              label="區間－迄"
              focused
              value={"2025/04/06"}
              fullWidth
              disabled
              variant="standard"
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  borderRadius: "4px",
                  padding: "4px 8px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(0, 0, 0, 0.6)",
                  fontSize: "0.85rem",
                },
                "& .MuiInputBase-input": {
                  color: "#000000 !important",
                  fontWeight: 500,
                  WebkitTextFillColor: "#000000 !important",
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#000000 !important",
                  color: "#000000 !important",
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottom: "none",
                },
              }}
            />
          </Box>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}>
            <Button
              sx={(theme) => ({
                ...style.textButtonStyle,
                color: theme.palette.error.main,
              })}>
              關閉區間分析
            </Button>
          </Box>
        </Box>
        <Box
          sx={{
            background: "#ffffff",
            borderRadius: "8px",
            width: "calc(50vw - 56px - 80px)",
            p: "40px",
            gap: "30px",
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backdropFilter: "blur(8px)",
              backgroundColor: "rgba(255, 255, 255, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 10,
              borderRadius: "8px",
            }}>
            <Typography
              sx={{
                color: "#000000",
                fontSize: "24px",
                fontWeight: 600,
              }}>
              未開放本服務
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: "30px",
              justifyContent: "center",
              alignItems: "center",
            }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                justifyContent: "center",
                alignItems: "center",
              }}>
              <Typography
                sx={{
                  letterSpacing: "0.15px",
                  fontSize: "12px",
                  lineHeight: "12px",
                  fontWeight: 400,
                }}>
                [ 電表編號 P0001a ]
              </Typography>
              <Typography
                sx={{
                  letterSpacing: "0.15px",
                  fontSize: "12px",
                  lineHeight: "12px",
                  fontWeight: 400,
                  ">span": {
                    color: "#00B388",
                    fontSize: "30px",
                    lineHeight: "24px",
                    fontWeight: 700,
                  },
                }}>
                區間用電{" "}
                <span>{17500 + 18200 + 16800 + 3000 + randomNumber}</span> kWH
              </Typography>
              <Typography
                sx={{
                  letterSpacing: "0.15px",
                  fontSize: "12px",
                  lineHeight: "12px",
                  fontWeight: 400,
                  ">span": {
                    fontSize: "16px",
                    lineHeight: "24px",
                    fontWeight: 700,
                  },
                }}>
                電表度數 <span>3,562.26</span> kWH
              </Typography>
            </Box>
            <Box sx={{ width: "15vw", height: "150px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f5" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[15000, 20000]}
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    tickFormatter={(value: number) => `${value / 1000}k`}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#00B388"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </Box>
          <Box sx={{ width: "100%", display: "flex", gap: "16px" }}>
            <TextField
              size="small"
              label="區間－起"
              focused
              value={"2023/03/01 00:00:00"}
              fullWidth
              disabled
              variant="standard"
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  borderRadius: "4px",
                  padding: "4px 8px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(0, 0, 0, 0.6)",
                  fontSize: "0.85rem",
                },
                "& .MuiInputBase-input": {
                  color: "#000000 !important",
                  fontWeight: 500,
                  WebkitTextFillColor: "#000000 !important",
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#000000 !important",
                  color: "#000000 !important",
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottom: "none",
                },
              }}
            />
            <TextField
              size="small"
              label="區間－迄"
              focused
              value={"2023/03/01 00:00:00"}
              fullWidth
              disabled
              variant="standard"
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  borderRadius: "4px",
                  padding: "4px 8px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(0, 0, 0, 0.6)",
                  fontSize: "0.85rem",
                },
                "& .MuiInputBase-input": {
                  color: "#000000 !important",
                  fontWeight: 500,
                  WebkitTextFillColor: "#000000 !important",
                },
                "& .MuiInputBase-input.Mui-disabled": {
                  WebkitTextFillColor: "#000000 !important",
                  color: "#000000 !important",
                },
                "& .MuiInput-underline:before": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:after": {
                  borderBottom: "none",
                },
                "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                  borderBottom: "none",
                },
              }}
            />
          </Box>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}>
            <Button
              sx={(theme) => ({
                ...style.textButtonStyle,
                color: theme.palette.error.main,
              })}>
              關閉區間分析
            </Button>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          width: "calc(100vw - 80px)",
          display: "grid",
          gap: "32px",
          gridTemplateColumns: "repeat(4, 1fr)",
        }}>
        {[
          {
            a: 9037,
            c: "#FBBB0A",
            x: 65012,
          },
          {
            a: 11099,
            c: "#FBBB0A",
            x: 63526,
          },
          {
            a: 15012,
            c: "#00B388",
            x: 65789,
          },
          {
            a: 13015,
            c: "#00B388",
            x: 67552,
              },
          {
            a: 7566,
            c: "#00B388",
            x: 65761,
          },
        ].map((_, index) => (
          <InfoBox
            key={index}
            a={_.a}
            x={_.x}
            index={(index + 1).toString()}
            color={_.c}
          />
        ))}
      </Box>
    </Box>
  );
};

const InfoBox = (props: { color: string; a: number; index: string; x: number }) => {
  const { color, index, a, x } = props;
  const [randomNumber, setRandomNumber] = useState(0);


  console.log(index)
  console.log(randomNumber);

  useEffect(() => {
    const generateRandomNumber = () => {
      return Math.floor(Math.random()) ;
    };

    const generateRandomInterval = () => {
      return Math.floor(Math.random() * 4001) + 1000;
    };

    const updateNumber = () => {
      setRandomNumber((val) => val + generateRandomNumber());
      const newInterval = generateRandomInterval();
      setTimeout(updateNumber, newInterval); // Use setTimeout instead of setInterval
    };

    updateNumber();
  }, [a]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        justifyContent: "center",
        alignItems: "center",
        background: "#ffffff",
        padding: "10px 40px",
        borderRadius: "8px",
        width: "calc(25vw - 56px - 80px)",
      }}>
      <Typography
        sx={{
          letterSpacing: "0.15px",
          fontSize: "12px",
          lineHeight: "12px",
          fontWeight: 400,
        }}>
        [ 電表 ]
      </Typography>
      <Typography
        sx={{
          letterSpacing: "0.15px",
          fontSize: "12px",
          lineHeight: "12px",
          fontWeight: 400,
          ">span": {
            color: color,
            fontSize: "30px",
            lineHeight: "24px",
            fontWeight: 700,
          },
        }}>
        區間用電 <span>{a}</span> kWH
      </Typography>
      <Typography
        sx={{
          letterSpacing: "0.15px",
          fontSize: "12px",
          lineHeight: "12px",
          fontWeight: 400,
          ">span": {
            fontSize: "16px",
            lineHeight: "24px",
            fontWeight: 700,
          },
        }}>
        電表度數 <span>{x}</span> kWH
      </Typography>
      <Box>
        <Button
          sx={(theme) => ({
            ...style.textButtonStyle,
            color: theme.palette.primary.main,
          })}>
          開啟區間分析
        </Button>
      </Box>
    </Box>
  );
};

export default Dashboard;
